import { useCallback, useEffect, useRef, useState } from "react";
import { getSubmission } from "@/features/efiling/api";
import type { IRSSubmissionDetail } from "@/features/efiling/types";
import { ApiError } from "@/lib/api";

interface Result {
  submission: IRSSubmissionDetail | null;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

/** Fetches one submission with its lifecycle events; race-guarded. */
export function useSubmission(clientId: string, id: string): Result {
  const [submission, setSubmission] = useState<IRSSubmissionDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  const lastKeyRef = useRef<string | null>(null);
  const requestIdRef = useRef(0);
  const mountedRef = useRef(true);

  const refetch = useCallback(() => setReloadKey((k) => k + 1), []);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    const key = `${reloadKey}|${clientId}|${id}`;
    if (lastKeyRef.current === key) return;
    lastKeyRef.current = key;

    const reqId = ++requestIdRef.current;
    setLoading(true);
    setError(null);

    getSubmission(clientId, id)
      .then((result) => {
        if (!mountedRef.current || requestIdRef.current !== reqId) return;
        setSubmission(result);
      })
      .catch((err) => {
        if (!mountedRef.current || requestIdRef.current !== reqId) return;
        setSubmission(null);
        setError(err instanceof ApiError ? err.message : "Failed to load the submission.");
      })
      .finally(() => {
        if (mountedRef.current && requestIdRef.current === reqId) setLoading(false);
      });
  }, [clientId, id, reloadKey]);

  return { submission, loading, error, refetch };
}
